import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LogOut,
  FileText,
  Clock,
  Plus,
  Edit,
  Trash2,
  Users,
  MousePointerClick,
  UserPlus,
  TrendingUp,
  MessageSquare,
  MessageCircle,
  Cpu,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const DATE_RANGE_OPTIONS = [7, 30, 90];

function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [recentActivity, setRecentActivity] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [instructionCount, setInstructionCount] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);
  const [usersModalOpen, setUsersModalOpen] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersSnapshot, setUsersSnapshot] = useState([]);
  const [topPagesModalOpen, setTopPagesModalOpen] = useState(false);
  const [guestVisitorsModalOpen, setGuestVisitorsModalOpen] = useState(false);
  const [guestVisitorsLoading, setGuestVisitorsLoading] = useState(false);
  const [guestVisitors, setGuestVisitors] = useState([]);

  useEffect(() => {
    fetchRecentActivity();
    fetchInstructionCount();
  }, []);

  useEffect(() => {
    fetchAnalyticsSummary(selectedDays);
  }, [selectedDays]);

  const fetchRecentActivity = async () => {
    try {
      const { data } = await axios.get("/api/prompt-admin/audit?limit=10");
      setRecentActivity(data.logs || []);
    } catch (error) {
      console.error("Failed to fetch recent activity:", error);
    } finally {
      setActivityLoading(false);
    }
  };

  const fetchInstructionCount = async () => {
    try {
      const { data } = await axios.get("/api/prompt-admin/list");
      setInstructionCount(data.count ?? data.files?.length ?? 0);
    } catch (error) {
      console.error("Failed to fetch instruction count:", error);
    }
  };

  const fetchAnalyticsSummary = async (days) => {
    setAnalyticsLoading(true);
    try {
      const { data } = await axios.get(`/api/analytics/summary?days=${days}`);
      setAnalytics(data);
    } catch (error) {
      console.error("Failed to fetch analytics summary:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchGuestVisitors = async () => {
    setGuestVisitorsLoading(true);
    try {
      const { data } = await axios.get(
        `/api/analytics/guest-visitors?days=${selectedDays}&limit=200`
      );
      setGuestVisitors(data.visitors || []);
      setGuestVisitorsModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch guest visitors:", error);
    } finally {
      setGuestVisitorsLoading(false);
    }
  };

  const fetchUsersSnapshot = async () => {
    setUsersLoading(true);
    try {
      const { data } = await axios.get("/api/analytics/users?limit=100");
      setUsersSnapshot(data.users || []);
      setUsersModalOpen(true);
    } catch (error) {
      console.error("Failed to fetch users snapshot:", error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getActionBadge = (action) => {
    switch (action) {
      case "create":
        return {
          icon: Plus,
          label: "Created",
          className: "bg-success-100 text-success-800",
        };
      case "update":
        return {
          icon: Edit,
          label: "Updated",
          className: "bg-primary-100 text-primary-800",
        };
      case "delete":
        return {
          icon: Trash2,
          label: "Deleted",
          className: "bg-danger-100 text-danger-800",
        };
      default:
        return {
          icon: Clock,
          label: action,
          className: "bg-gray-100 text-gray-800",
        };
    }
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-semibold text-gray-900">
                EqualJustice Admin
              </h1>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.email}
              </span>
              <button
                onClick={handleLogout}
                className="btn-secondary flex items-center"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Dashboard</h2>

          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Traffic & Signup Analytics
              </h3>
              <div className="flex items-center gap-2">
                {DATE_RANGE_OPTIONS.map((days) => (
                  <button
                    key={days}
                    onClick={() => setSelectedDays(days)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      selectedDays === days
                        ? "bg-primary-600 text-white border-primary-600"
                        : "bg-white text-gray-600 border-gray-200 hover:border-primary-300"
                    }`}
                  >
                    {days}d
                  </button>
                ))}
                <span className="text-xs text-gray-500">IST</span>
              </div>
            </div>

            {analyticsLoading ? (
              <div className="card p-6 text-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-3 text-gray-500 text-sm">
                  Loading analytics...
                </p>
              </div>
            ) : analytics?.kpis ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
                  <button
                    type="button"
                    onClick={fetchUsersSnapshot}
                    className="card p-4 text-left hover:shadow-md transition-shadow duration-200 disabled:opacity-60"
                    disabled={usersLoading}
                  >
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Total Users</p>
                      <Users className="h-4 w-4 text-primary-600" />
                    </div>
                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                      {analytics.kpis.totalUsers ?? 0}
                    </p>
                    <p className="text-xs text-primary-600 mt-2">
                      {usersLoading ? "Loading users..." : "Click to view users"}
                    </p>
                  </button>
                  <div className="card p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Unique Visitors</p>
                      <MousePointerClick className="h-4 w-4 text-success-600" />
                    </div>
                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                      {analytics.kpis.uniqueVisitors ?? 0}
                    </p>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Signups Completed</p>
                      <UserPlus className="h-4 w-4 text-indigo-600" />
                    </div>
                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                      {analytics.kpis.signupCompleted ?? 0}
                    </p>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">Conversion Rate</p>
                      <TrendingUp className="h-4 w-4 text-amber-600" />
                    </div>
                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                      {analytics.kpis.conversionRate ?? 0}%
                    </p>
                  </div>
                  <div className="card p-4">
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-gray-600">
                        Visitors Without Signup
                      </p>
                      <Clock className="h-4 w-4 text-rose-600" />
                    </div>
                    <p className="text-2xl font-semibold text-gray-900 mt-2">
                      {analytics.kpis.visitorsWithoutSignup ?? 0}
                    </p>
                  </div>
                </div>

                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Guest Chat Analytics
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="card p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">Guest Chats Started</p>
                        <MessageCircle className="h-4 w-4 text-teal-600" />
                      </div>
                      <p className="text-2xl font-semibold text-gray-900 mt-2">
                        {analytics.kpis.uniqueGuestChatStarters ?? 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Unique visitors who opened a new guest session
                      </p>
                    </div>
                    <div className="card p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">Guests Who Messaged</p>
                        <MessageSquare className="h-4 w-4 text-cyan-600" />
                      </div>
                      <p className="text-2xl font-semibold text-gray-900 mt-2">
                        {analytics.kpis.uniqueGuestMessengers ?? 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Unique visitors with at least one guest message
                      </p>
                    </div>
                    <div className="card p-4">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">Total Guest Messages</p>
                        <MessageSquare className="h-4 w-4 text-sky-600" />
                      </div>
                      <p className="text-2xl font-semibold text-gray-900 mt-2">
                        {analytics.kpis.totalGuestMessages ?? 0}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        User messages sent in guest chat
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={fetchGuestVisitors}
                      className="card p-4 text-left hover:shadow-md transition-shadow duration-200 disabled:opacity-60"
                      disabled={guestVisitorsLoading}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-600">Guest Engagement Rate</p>
                        <TrendingUp className="h-4 w-4 text-violet-600" />
                      </div>
                      <p className="text-2xl font-semibold text-gray-900 mt-2">
                        {analytics.kpis.guestMessageRate ?? 0}%
                      </p>
                      <p className="text-xs text-primary-600 mt-2">
                        {guestVisitorsLoading
                          ? "Loading visitors..."
                          : "Click to view per-visitor breakdown"}
                      </p>
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="card p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Signup Funnel
                    </h4>
                    <div className="space-y-3">
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            1. Unique Visitors
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {analytics.kpis.uniqueVisitors ?? 0}
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            2. Signup Started
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {analytics.kpis.signupStarted ?? 0}
                          </span>
                        </div>
                      </div>
                      <div className="rounded-lg bg-gray-50 p-3 border border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            3. Signup Completed
                          </span>
                          <span className="text-sm font-semibold text-gray-900">
                            {analytics.kpis.signupCompleted ?? 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="card p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Daily Guest Chat Trend
                    </h4>
                    {analytics.dailyGuestChat?.length ? (
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {analytics.dailyGuestChat.map((row) => (
                          <div
                            key={`guest-${row.date}`}
                            className="flex items-center justify-between text-sm border-b border-gray-100 pb-2"
                          >
                            <span className="text-gray-600">{row.date}</span>
                            <div className="text-right">
                              <p className="text-gray-900">
                                Guests: {row.uniqueGuests}
                              </p>
                              <p className="text-gray-500">
                                Chats: {row.chatsStarted} · Msgs: {row.messagesSent}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No guest chat data yet.
                      </p>
                    )}
                  </div>
                  <div className="card p-4">
                    <h4 className="text-sm font-semibold text-gray-900 mb-3">
                      Daily Traffic Trend
                    </h4>
                    {analytics.dailyTraffic?.length ? (
                      <div className="space-y-2 max-h-56 overflow-y-auto">
                        {analytics.dailyTraffic.map((row) => (
                          <div
                            key={row.date}
                            className="flex items-center justify-between text-sm border-b border-gray-100 pb-2"
                          >
                            <span className="text-gray-600">{row.date}</span>
                            <div className="text-right">
                              <p className="text-gray-900">
                                Visitors: {row.uniqueVisitors}
                              </p>
                              <p className="text-gray-500">
                                Views: {row.pageViews}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">
                        No traffic data yet.
                      </p>
                    )}
                  </div>
                </div>

                <div className="card p-5 mt-4">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="text-base font-semibold text-gray-900">
                        Top Pages
                      </h4>
                      <p className="text-sm text-gray-500 mt-1">
                        Most visited pages for the selected period
                      </p>
                    </div>
                    {analytics.topPages?.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setTopPagesModalOpen(true)}
                        className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                      >
                        View detailed list
                      </button>
                    )}
                  </div>

                  {analytics.topPages?.length ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Page Path
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Views
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Unique Visitors
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                          {analytics.topPages.slice(0, 5).map((page) => (
                            <tr key={page.path}>
                              <td className="px-4 py-3 text-sm text-gray-700 max-w-xl truncate">
                                {page.path}
                              </td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                {page.views}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {page.uniqueVisitors ?? 0}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">
                      No page data yet.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <div className="card p-6">
                <p className="text-sm text-gray-500">
                  Analytics data is not available right now.
                </p>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="card p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-primary-100 rounded-lg p-3">
                  <FileText className="h-6 w-6 text-primary-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Instructions
                  </h3>
                  <p className="text-sm text-gray-600">
                    {instructionCount !== null
                      ? `${instructionCount} prompt instruction${instructionCount !== 1 ? "s" : ""}`
                      : "Manage prompt instructions"}
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate("/instructions")}
                  className="btn-primary"
                >
                  Manage Instructions
                </button>
              </div>
            </div>

            <div className="card p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-success-100 rounded-lg p-3">
                  <Clock className="h-6 w-6 text-success-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Audit Log
                  </h3>
                  <p className="text-sm text-gray-600">
                    View all change history
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate("/audit")}
                  className="btn-primary"
                >
                  View Audit Log
                </button>
              </div>
            </div>

            <div className="card p-6 hover:shadow-lg transition-shadow duration-200">
              <div className="flex items-center">
                <div className="flex-shrink-0 bg-indigo-100 rounded-lg p-3">
                  <Cpu className="h-6 w-6 text-indigo-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Gemini AI
                  </h3>
                  <p className="text-sm text-gray-600">
                    Model, temperature, topK, topP
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <button
                  onClick={() => navigate("/gemini")}
                  className="btn-primary"
                >
                  Configure Gemini
                </button>
              </div>
            </div>
          </div>

          {/* Recent Activity - Real Data */}
          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Recent Activity
              </h3>
              {recentActivity.length > 0 && (
                <button
                  onClick={() => navigate("/audit")}
                  className="text-sm text-primary-600 hover:text-primary-800 font-medium"
                >
                  View all
                </button>
              )}
            </div>

            {activityLoading ? (
              <div className="text-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto"></div>
                <p className="mt-3 text-gray-500 text-sm">
                  Loading activity...
                </p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="text-center py-6">
                <Clock className="h-8 w-8 text-gray-300 mx-auto" />
                <p className="mt-2 text-gray-500 text-sm">
                  No activity recorded yet
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {recentActivity.map((log, index) => {
                  const badge = getActionBadge(log.action);
                  const ActionIcon = badge.icon;
                  return (
                    <div
                      key={log._id || index}
                      className={`flex items-center justify-between py-3 ${
                        index < recentActivity.length - 1
                          ? "border-b border-gray-100"
                          : ""
                      }`}
                    >
                      <div className="flex items-center min-w-0">
                        <ActionIcon className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0" />
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            <span className="font-semibold">
                              {log.identifier}
                            </span>
                            {" "}
                            was {log.action}d
                          </p>
                          <p className="text-xs text-gray-500">
                            by {log.performedBy || "unknown"} &middot;{" "}
                            {formatTimeAgo(log.createdAt)}
                          </p>
                        </div>
                      </div>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-3 ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </main>

      {usersModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Registered Users Snapshot
                </h3>
                <p className="text-sm text-gray-500">
                  Showing the latest {usersSnapshot.length} users
                </p>
              </div>
              <button
                type="button"
                onClick={() => setUsersModalOpen(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="overflow-y-auto max-h-[70vh]">
              {usersSnapshot.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  No registered users found.
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {usersSnapshot.map((snapshotUser, index) => (
                    <div
                      key={snapshotUser._id || `${snapshotUser.email}-${index}`}
                      className="px-6 py-4 flex items-center justify-between gap-4"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {snapshotUser.email}
                        </p>
                        <p className="text-xs text-gray-500">
                          Registered: {formatDateTime(snapshotUser.createdAt)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {guestVisitorsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Guest Chat Visitors
                </h3>
                <p className="text-sm text-gray-500">
                  Per anonymous visitor for the last {selectedDays} days (same ID as site analytics)
                </p>
              </div>
              <button
                type="button"
                onClick={() => setGuestVisitorsModalOpen(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="overflow-y-auto max-h-[70vh]">
              {guestVisitors.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  No guest chat activity in this period.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Anonymous ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Started Chat
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Messages
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Use Cases
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Last Activity
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {guestVisitors.map((visitor) => (
                        <tr key={visitor.anonymousId}>
                          <td className="px-6 py-4 text-sm text-gray-700 font-mono break-all max-w-xs">
                            {visitor.anonymousId}
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <span
                              className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                                visitor.startedChat
                                  ? "bg-teal-100 text-teal-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {visitor.startedChat ? "Yes" : "No"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {visitor.messageCount ?? 0}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {(visitor.assistantKeys || []).join(", ") || "—"}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatDateTime(visitor.lastActivityAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {topPagesModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[85vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Top Pages Detail
                </h3>
                <p className="text-sm text-gray-500">
                  Detailed traffic snapshot for the last {selectedDays} days
                </p>
              </div>
              <button
                type="button"
                onClick={() => setTopPagesModalOpen(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close
              </button>
            </div>

            <div className="overflow-y-auto max-h-[70vh]">
              {analytics?.topPages?.length ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Page Path
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Views
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Unique Visitors
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {analytics.topPages.map((page) => (
                        <tr key={`detail-${page.path}`}>
                          <td className="px-6 py-4 text-sm text-gray-700 break-all">
                            {page.path}
                          </td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">
                            {page.views}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {page.uniqueVisitors ?? 0}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-10 text-center text-sm text-gray-500">
                  No page data available.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
